import { Link } from 'react-router-dom';
import { hasProFeatures } from '@/utils/entitlements';
import { Card } from '@/components';
import { useVehicleSummary, useEntitlement } from '@/hooks/useProApi';

const reportButtons = [
  {
    type: 'emissions',
    label: 'Emissions Report',
    color: 'from-green-600 to-green-500',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    type: 'diagnostic',
    label: 'Diagnostic Report',
    color: 'from-blue-600 to-blue-500',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    type: 'pre_purchase',
    label: 'Pre-Purchase Report',
    color: 'from-orange-600 to-orange-500',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    type: 'estimate',
    label: 'Estimate Report',
    color: 'from-purple-600 to-purple-500',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
  },
];

interface AIDiagnosticSummaryProps {
  vin: string;
}

export function AIDiagnosticSummary({ vin }: AIDiagnosticSummaryProps) {
  const { data, isLoading, error } = useVehicleSummary(vin);
  const { data: entitlement } = useEntitlement();
  const isPro = hasProFeatures(entitlement?.plan);

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-white font-bold">ARIA Diagnostic Summary</h3>
      </div>

      {/* Summary text */}
      {isLoading ? (
        <div className="space-y-3 mb-6">
          <div className="h-4 bg-gray-700/50 rounded animate-pulse w-full" />
          <div className="h-4 bg-gray-700/50 rounded animate-pulse w-5/6" />
          <div className="h-4 bg-gray-700/50 rounded animate-pulse w-4/6" />
        </div>
      ) : error ? (
        <p className="text-gray-500 text-sm mb-6">Summary unavailable</p>
      ) : data?.summary ? (
        <p className="text-gray-300 text-sm leading-relaxed mb-6">{data.summary}</p>
      ) : (
        <p className="text-gray-500 text-sm mb-6">
          No diagnostic conversations yet. Chat with ARIA in the mobile app to build vehicle history.
        </p>
      )}

      {/* Report type buttons */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {reportButtons.map((btn) =>
          isPro ? (
            <Link
              key={btn.type}
              to={`/reports/generate?vin=${encodeURIComponent(vin)}&type=${btn.type}`}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r ${btn.color} text-white text-sm font-medium hover:shadow-lg transition hover:scale-[1.02]`}
            >
              {btn.icon}
              <span className="truncate">{btn.label}</span>
            </Link>
          ) : (
            <div
              key={btn.type}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r ${btn.color} text-white text-sm font-medium opacity-50 pointer-events-none`}
            >
              {btn.icon}
              <span className="truncate">{btn.label}</span>
            </div>
          )
        )}
      </div>
    </Card>
  );
}
